import React, { useState, useEffect } from 'react'

import cn from 'classnames'
import pluralize from 'pluralize'
import styled from 'styled-components'
import { Card, themeConWeb } from '@foodsby/nutrient'
import { DynamicLink } from '../common/DynamicLink'
import { api, FOODSBY_BASE_URL } from '../../api/api'
import subDays from 'date-fns/sub_days'
import format from 'date-fns/format'
import { formatUrl } from '../../util/formatUtils'
import { placeOrderRoute } from '../../routes/routes'
import { useSelector } from 'react-redux'
import { space } from 'styled-system'
import { LocationLogo } from '../common/Location'

// Functionality for displaying Friends who have ordered from a given restaurant that day.
const FriendsOrdered = ({ friendImageUrlBase, friendsOrdered }) => {
  const maxFriendBubbles = 6

  return (
    <ul className="-friendsOrdered">
      {friendsOrdered && friendsOrdered.length > maxFriendBubbles && (
        <li key="plusFriends">
          <div className="-friendOrdered -friendOrdered--plusFriends">
            {`+${friendsOrdered.length - maxFriendBubbles}`}
          </div>
        </li>
      )}
      {friendsOrdered.slice(0, maxFriendBubbles).map(friendOrdered => {
        const name = `${friendOrdered.firstName} ${friendOrdered.lastName}`
        return (
          <li key={friendOrdered.userId}>
            {friendOrdered.avatarId ? (
              <picture>
                <source
                  alt={name}
                  srcSet={encodeURI(
                    `${friendImageUrlBase}/profiles/${friendOrdered.avatarId}.webp?height=50&width=50`,
                  )}
                  title={`${friendOrdered.firstName} ${friendOrdered.lastName}`}
                  type="image/webp"
                />
                <source
                  alt={name}
                  srcSet={encodeURI(
                    `${friendImageUrlBase}/profiles/${friendOrdered.avatarId}.jpg?height=50&width=50`,
                  )}
                  title={`${friendOrdered.firstName} ${friendOrdered.lastName}`}
                  type="image/jpeg"
                />
                <img
                  alt={name}
                  className="-friendOrdered"
                  src={encodeURI(
                    `${friendImageUrlBase}/profiles/${friendOrdered.avatarId}.jpg?height=50&width=50`,
                  )}
                  title={`${friendOrdered.firstName} ${friendOrdered.lastName}`}
                />
              </picture>
            ) : (
              <div
                className="-friendOrdered -friendOrdered--noAvatarId"
                title={`${friendOrdered.firstName} ${friendOrdered.lastName}`}
              >
                {`${friendOrdered.firstName[0].toUpperCase()}${friendOrdered.lastName[0].toUpperCase()}`}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export const CardDropOff = ({
  children,
  classNames,
  delivery,
  friendsOrdered,
  ordersRemaining,
  p = 0,
  restaurantDetails,
  friendImageUrlBase,
  ...rest
}) => {
  const shortFormat = 'h:mm A'
  return (
    <Component className={cn(classNames)}>
      <Card className="-card" p={p} {...rest}>
        <div className="-limitedOrders">
          Hurry, only {pluralize('order', ordersRemaining, true)} left!
        </div>
        <div className="-restaurantLogoContainer">
          <LocationLogo
            className="-restaurantLogo"
            logoAlt={`${restaurantDetails.restaurantName} logo`}
            logoLink={restaurantDetails.logo}
          />
        </div>
        <div className="-restaurantDetails">
          <h2 className="-title">{restaurantDetails.restaurantName}</h2>
          <div className="-times">
            <div className="-orderTime">
              <p className="-timeTitle">Order by</p>
              <p className="-time">
                {format(restaurantDetails.cutoffTime, shortFormat)}
              </p>
            </div>
            <div className="-deliveryTime">
              <div>
                <p className="-timeTitle">Delivers at</p>
                <p className="-time">
                  {format(restaurantDetails.deliverTime, shortFormat)}
                </p>
              </div>
            </div>
          </div>
          <div className="-soldOutContent">
            <p className="-soldOutTitle">Sold Out!</p>
            <p className="-soldOutCopy">
              This restaurant only accepts a certain amount of orders to ensure
              quality.
            </p>
          </div>
          <div className="-tooLateContent">
            <p className="-tooLateTitle">Sorry, it’s too late.</p>
            <p className="-tooLateCopy">
              This restaurant stopped accepting orders at{' '}
              {format(restaurantDetails.cutoffTime, shortFormat)}.
            </p>
          </div>
          <p className="-cutOffContent">
            Hurry, <span className="--italics">thyme</span> is running out!
          </p>
          {friendsOrdered && (
            <FriendsOrdered
              friendImageUrlBase={friendImageUrlBase}
              friendsOrdered={friendsOrdered}
            />
          )}
        </div>
      </Card>
    </Component>
  )
}

export const dayFormat = 'D'
export const timeFormat = 'HHmm'

const date = new Date()
const isLimitedOrders = delivery =>
  delivery.weightLeft < 7 && delivery.weightLeft > 0
const isSoldOut = delivery => delivery.weightLeft <= 0
const isNearCutOff = delivery =>
  format(delivery.cutoffDate, timeFormat) - format(date, timeFormat) < 16 &&
  format(delivery.cutoffDate, timeFormat) - format(date, timeFormat) > 0 &&
  Number(format(delivery.cutoffDate, dayFormat)) === date.getDate()

const Delivery = ({ locationId, selectDelivery, selectedDate, deliveries }) => {
  const [friendsOrdered, setFriendsOrdered] = useState(null)
  const [mounted, setMounted] = useState(true)
  const isAuthenticated = useSelector(state => state?.user?.isAuthenticated)

  const cardDropoffClassName = delivery =>
    delivery.isPastCutoff
      ? 'is-tooLate'
      : isSoldOut(delivery)
      ? 'is-soldOut'
      : isNearCutOff(delivery)
      ? 'is-nearCutOff'
      : isLimitedOrders(delivery)
      ? 'is-limitedOrders'
      : delivery.isNewRestaurantAtLocation
      ? 'is-new'
      : ''

  const getFriendsOrdered = async (storeIds, fromDate, toDate) => {
    const res = await api.get(
      `${FOODSBY_BASE_URL}/api/v1/connections/friends/orders/stores?storeIds=${storeIds}&fromDate=${fromDate}&toDate=${toDate}`,
      null,
      { cache: true },
    )
    if (mounted) {
      setFriendsOrdered(res.friendsOrderedFromDate)
    }
  }

  useEffect(() => {
    const onDate = format(subDays(selectedDate, 0), 'YYYY-MM-DD')
    const storeIds = deliveries.map(d => d.storeId)

    isAuthenticated && getFriendsOrdered(storeIds, onDate, onDate)

    return () => {
      setMounted(false)
    }
  }, [deliveries]) //eslint-disable-line

  return deliveries.map(delivery => {
    return (
      <DynamicLink
        className={cn('no-hover', {
          'Link--is-inActive': isSoldOut(delivery) || delivery.isPastCutoff,
        })}
        isActive={!delivery.isPastCutoff && !isSoldOut(delivery)}
        key={delivery.deliveryId}
        onClick={() => selectDelivery(delivery)}
        style={{ marginBottom: '16px' }}
        to={formatUrl(placeOrderRoute.path, {
          locationId: locationId,
          dropoffId: delivery.deliveryDropoffId,
        })}
      >
        <CardDropOff
          classNames={cardDropoffClassName(delivery)}
          friendImageUrlBase={process.env.REACT_APP_IMAGE_LAMBDA}
          friendsOrdered={
            friendsOrdered?.find(f => f.storeId === delivery.storeId)
              ?.friendsOrdered
          }
          ordersRemaining={delivery.weightLeft}
          restaurantDetails={{
            logo: delivery.logoUrl,
            restaurantName: delivery.restaurantName,
            cutoffTime: delivery.cutoffDate,
            deliverTime: delivery.dropoffDate,
          }}
        />
      </DynamicLink>
    )
  })
}

const Component = styled.div`
  ${space};
  display: block;
  // Hack to overwrite Nutrient V2
  font-family: ${themeConWeb.fontFamily} !important;
  flex: auto;
  max-width: 100%;
  position: relative;

  & .-card {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    height: 200px;
    padding-right: calc(${themeConWeb.baseUnit} * 8);
    flex-wrap: nowrap;

    &:hover {
      border-color: ${themeConWeb.color.brandPrimary};
    }
  }

  & .-restaurantLogoContainer {
    display: flex;
    height: inherit;
    justify-content: center;
    padding: calc(${themeConWeb.baseUnit} * 8);
    flex-basis: 45%;
  }

  & .-restaurantLogo {
    align-self: center;
    max-height: 150px;
    max-width: 200px;
  }

  & .-restaurantDetails {
    display: flex;
    flex-direction: column;
    flex-basis: 55%;
  }

  & .-title {
    color: ${themeConWeb.color.brandPrimary};
    font-size: 24px;
    font-weight: 500;
    margin-bottom: calc(${themeConWeb.baseUnit} * 2);
  }

  & .-times {
    display: flex;
    max-width: 250px;
    margin-bottom: calc(${themeConWeb.baseUnit} * 4);
  }

  & .-orderTime {
    border-right: 1px solid ${themeConWeb.color.greyShade2};
    width: 50%;
  }

  & .-deliveryTime {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    width: 50%;
  }

  & .-timeTitle {
    color: ${themeConWeb.primaryType};
    font-size: 14px;
    text-align: left;
    margin-bottom: 4px;
  }

  & .-time {
    color: ${themeConWeb.primaryType};
    font-size: 21px;
  }

  & .-timeAbbr {
    font-size: 16px;
  }

  // TODO Friends component Split out into state
  & .-friendsOrdered {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    margin-left: 0px;
  }

  & .-friendOrdered {
    border: 2px solid ${themeConWeb.color.white};
    border-radius: 50%;
    width: calc(${themeConWeb.baseUnit} * 10);
    height: calc(${themeConWeb.baseUnit} * 10);
    &:not(last-child) {
      margin-left: -${themeConWeb.baseUnit};
    }

    // Class modifiers
    &--noAvatarId {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: ${themeConWeb.color.greyShade2};
      color: ${themeConWeb.color.greyShade5};
      font-size: ${themeConWeb.fontSizes.base};
      &:not(last-child) {
        margin-left: -${themeConWeb.baseUnit};
      }
    }

    &--plusFriends {
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid ${themeConWeb.color.greyShade4};
      background-color: ${themeConWeb.color.white};
      color: ${themeConWeb.color.greyShade4};
      font-size: 12px;
      width: calc(4px * 9);
      height: calc(4px * 9);
      margin-top: 4px;
    }
  }
  // responsive styles
  @media screen and ${themeConWeb.small} {
    & .-card {
      height: auto;
      padding: 0 calc(${themeConWeb.baseUnit} * 8)
        calc(${themeConWeb.baseUnit} * 2);
      flex-wrap: wrap;
    }

    & .-title {
      font-size: 18px;
    }

    & .-restaurantLogoContainer {
      justify-content: center;
      width: 100%;
      flex-basis: 100%;
    }

    & .-restaurantDetails {
      width: 100%;
      flex-basis: 100%;
    }
  }
  /** States
  *
  * Hiding initial states
  *
  **/
  & .-limitedOrders,
  & .-restaurantDetails::before,
  & .-soldOutContent,
  & .-tooLateContent,
  & .-cutOffContent {
    display: none;
  }

  &.is-new {
    & .-restaurantDetails::before {
      border-radius: ${themeConWeb.borderRadius};
      content: 'NEW';
      color: ${themeConWeb.color.white};
      background-color: ${themeConWeb.color.success};
      display: flex;
      justify-content: center;
      background-color: ${themeConWeb.success};
      font-size: ${themeConWeb.fontSizes.baseMinus1};
      padding: calc(${themeConWeb.baseUnit} * 2)
        calc(${themeConWeb.baseUnit} * 2);
      width: 55px;
      position: absolute;
      left: -${themeConWeb.baseUnit};
      top: -${themeConWeb.baseUnit};
      font-weight: 500;
    }
  }

  &.is-limitedOrders {
    .-limitedOrders {
      font-size: ${themeConWeb.fontSizes.base};
      background-color: ${themeConWeb.color.white};
      border: 1px solid ${themeConWeb.color.brandPrimary};
      border-radius: ${themeConWeb.borderRadius};
      color: ${themeConWeb.color.brandPrimary};
      display: block;
      padding: calc(${themeConWeb.baseUnit} + 2px)
        calc(${themeConWeb.baseUnit} * 2.5);
      position: absolute;
      left: -${themeConWeb.baseUnit};
      top: -${themeConWeb.baseUnit};
      font-weight: 500;
    }
  }

  &.is-soldOut .-soldOutContent {
    display: block;
  }

  &.is-tooLate {
    & .-card:hover {
      cursor: default;
      border-color: ${themeConWeb.color.greyShade2};
    }

    & .-tooLateContent {
      display: block;
    }
  }

  &.is-soldOut,
  &.is-tooLate {
    & .-restaurantLogoContainer,
    & .-friendsOrdered {
      opacity: 0.5;
    }

    & .-title {
      opacity: 50%;
    }

    & .-restaurantDetails::before {
      opacity: 50%;
    }

    & .-times {
      display: none;
    }
    & .-soldOutContent,
    & .-tooLateContent {
      margin-bottom: calc(${themeConWeb.baseUnit} * 4);
    }

    & .-soldOutTitle,
    & .-tooLateTitle {
      font-weight: bold;
      color: ${themeConWeb.color.dangerLight};
      font-size: ${themeConWeb.fontSizes.basePlus1};
      margin-bottom: ${themeConWeb.baseUnit};
    }

    & .-soldOutCopy,
    & .-tooLateCopy {
      font-size: ${themeConWeb.fontSizes.baseMinus1};
    }
  }

  &.is-nearCutOff {
    & .-orderTime .-time,
    & .-orderTime .-timeTitle {
      color: ${themeConWeb.color.dangerLight};
    }

    & .-cutOffContent {
      font-size: ${themeConWeb.fontSizes.base};
      background-color: ${themeConWeb.color.white};
      border: 1px solid ${themeConWeb.color.dangerLight};
      border-radius: ${themeConWeb.borderRadius};
      color: ${themeConWeb.color.dangerLight};
      display: block;
      padding: calc(${themeConWeb.baseUnit} + 2px)
        calc(${themeConWeb.baseUnit} * 2.5);
      position: absolute;
      left: -${themeConWeb.baseUnit};
      top: -${themeConWeb.baseUnit};
      font-weight: 500;

      & .--italics {
        color: ${themeConWeb.color.dangerLight};
        font-style: italic;
      }
    }
  }
`
export default Delivery
